<template>
    <div class="train-numbers-main-wrapper">
        <!-- Error template -->
        <template v-if="errorOccurred">
            <div class="d-flex justify-content-center align-items-center w-100">
                <p class="fs-5 text-center mb-0">
                    <span
                        v-if="errorMessage"
                        v-html="errorMessage"
                    />
                    <template v-else>
                        Произошла ошибка. Пожалуйста,
                        <a
                            class="link"
                            href=""
                            onclick="location.reload()"
                        >обновите страницу</a>.
                    </template>
                </p>
            </div>
        </template>

        <template v-else>
            <!-- Question Number -->
            <span
                v-if="questions?.length && !quizDone"
                class="question-number"
            >
                {{ currentQuestionIndex + 1 }} / {{ questions.length }}
            </span>

            <!-- Open Settings Button -->
            <a
                class="settings-open"
                href=""
                @click.prevent="settingsOpen()"
            >
                <i class="fa fa-gear text-secondary fs-5"/>
            </a>

            <!-- Settings -->
            <train-numbers-settings @settings-saved="settingsSaved"/>

            <!-- Loading Spinner -->
            <div
                v-if="loading.isActive"
                class="d-flex flex-column justify-content-center align-items-center w-100"
            >
                <span class="spinner spinner-big"/>
                <span class="d-inline-block text-secondary mt-2">
                    {{ loading.message }}<span class="position-absolute">{{ loading.dots }}</span>
                </span>
            </div>

            <!-- Main Questions Wrapper -->
            <div
                v-else-if="questions.length && !quizDone"
                class="col m-auto px-lg-4 px-xl-5"
            >
                <!-- Question -->
                <p class="train-question mb-4">
                    {{ currentQuestion.text }}
                </p>

                <div
                    :class="`${currentInputMode}-mode`"
                    class="answer-wrapper mx-auto mb-3"
                >
                    <!-- Answer Form -->
                    <div class="d-flex align-items-center gap-2 my-1">
                        <input
                            id="number-input"
                            v-imask="{mask: inputModes[currentInputMode]?.regex}"
                            class="form-control"
                            :value="answerToCheck"
                            :disabled="isCurrentQuestionAnswered"
                            :placeholder="inputModes[currentInputMode]?.placeholder"
                            autocomplete="off"
                            @accept="inputMaskOnAccept"
                        >
                        <!-- Check answer -->
                        <button
                            :class="{'disabled': isCurrentQuestionAnswered}"
                            class="btn btn-primary"
                            @click.prevent="checkAnswer()"
                        >
                            <i class="fa fa-arrow-right"/>
                        </button>
                    </div>

                    <!-- Wrong Answer Wrapper -->
                    <div
                        v-if="isCurrentQuestionAnswered"
                        class="wrong-answer-wrapper"
                    >
                        <div
                            v-if="!currentQuestion.answerCorrect"
                            class="position-absolute"
                        >
                            <p class="mb-1 fw-bold d-flex align-items-center gap-2">
                                <i class="fa fa-close text-danger fs-4 pt-1"/> Неправильно
                            </p>
                            <p class="fw-bold mb-2">
                                Правильный ответ: <span class="fs-3 ms-1">{{ currentQuestion.index }}</span>
                            </p>
                            <p>
                                <button
                                    class="btn btn-primary next-question"
                                    @click="goToNextQuestion()"
                                >
                                    Дальше
                                </button>
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Results wrapper -->
            <template v-else-if="quizDone">
                <div class="text-center col m-auto w-100">
                    <p class="fs-4 fw-bold mb-1 mb-sm-2">
                        Правильных ответов:
                    </p>
                    <p class="fs-3 fw-bold mb-0">
                        {{ questions.filter(el => el.answerCorrect).length }} / {{ questions.length }}
                    </p>
                    <div class="results-table-outer-wrapper">
                        <div class="results-table-link-wrapper show">
                            <a
                                class="text-secondary"
                                href=""
                                @click.prevent="toggleResultsTable()"
                            >
                                Показать подробности
                            </a>
                        </div>
                        <!-- Quiz stats -->
                        <div
                            class="results-table-wrapper"
                            style="display: none"
                        >
                            <table class="table table-striped">
                                <thead class="table-light">
                                    <tr>
                                        <th>#</th>
                                        <th>Вопрос</th>
                                        <th>Ответ</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr
                                        v-for="(question, index) in questions"
                                        :key="question.index"
                                    >
                                        <!-- Question number -->
                                        <td>{{ index + 1 }}</td>

                                        <!-- Question -->
                                        <td>{{ question[questionModes[currentQuestionMode].questionProp] }}</td>

                                        <!-- Answer -->
                                        <td>
                                            <!-- Correct answer -->
                                            <p
                                                v-if="question.answerCorrect"
                                                :class="`${currentInputMode}-mode`"
                                                class="alert alert-success results-answer"
                                            >
                                                {{ question.answer }}
                                            </p>
                                            <template v-else>
                                                <!-- Wrong answer -->
                                                <p
                                                    :class="`${currentInputMode}-mode`"
                                                    class="alert alert-danger results-answer"
                                                >
                                                    {{ question.answer }}
                                                </p>
                                                <!-- Correct answer -->
                                                <p
                                                    :class="`${currentInputMode}-mode`"
                                                    class="alert alert-success results-answer ms-2"
                                                >
                                                    {{ question[inputModes[currentInputMode].answerProp] }}
                                                </p>
                                            </template>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <!-- Start over again -->
                    <button
                        class="btn btn-primary start-over"
                        @click="startOver()"
                    >
                        Начать заново
                    </button>
                </div>

                <!-- Fireworks -->
                <div class="train-fireworks"/>
            </template>
        </template>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import { IMaskDirective } from 'vue-imask'
import TrainNumbersSettings from './TrainNumbersSettings.vue'
import { Fireworks } from 'fireworks-js'

export default {
    name: 'TrainNumbers',
    components: {
        TrainNumbersSettings
    },
    directives: {
        imask: IMaskDirective
    },
    data() {
        return {
            currentQuestionIndex: null,
            questions: [],
            answerToCheck: '',
            quizDone: false,
            abortController: null,
            errorOccurred: false,
            errorMessage: '',
            loading: {
                isActive: false,
                message: '',
                dots: '',
                dotsTimeout: null,
            },
            fireworks: null,
        }
    },
    computed: {
        ...mapState('trainNumbersModule', [
            'params',
            'questionModes',
            'inputModes',
            'currentQuestionMode',
            'currentInputMode',
        ]),
        // Get the current question
        currentQuestion() {
            return this.questions[this.currentQuestionIndex] ?? {}
        },
        // Check if the current question is answered
        isCurrentQuestionAnswered() {
            return this.currentQuestion.answer?.length > 0
        }
    },
    watch: {
        // When changing the question, load data for the following numbers
        currentQuestionIndex() {
            // Get index of the closest number without an audio
            let index = this.questions.findIndex((el) => !el.tts)
            if (index !== -1) {
                if (index - this.currentQuestionIndex === 0) { // If this number is in the current question
                    this.startLoading('Загрузка аудио')
                } else if (index - this.currentQuestionIndex < 5) { // If this number comes in less than 5
                    // If request is active already, then prevent the new one
                    if (this.abortController) {
                        return
                    }
                    this.abortController = new AbortController()
                    // Get transcriptions of numbers that require audio
                    let neededNumbers = this.questions.slice(index, index + 5).map(e => e.transcription)
                    // If recaptcha has not been initialized, show an error and skip the request
                    if (!grecaptcha) {
                        this.errorOccurred = true
                        this.stopLoading()
                        return
                    }
                    grecaptcha.ready(() => {
                        grecaptcha.execute('6Lcbo5MkAAAAAB8UQm-cCOn8oWoLMUoan1b5yN-u', {action: 'train_numbers'})
                            .then((recaptchaToken) => {
                                axios.post(
                                    '/api/training/numbers/generate-additional-audio',
                                    {
                                        ...this.params,
                                        neededNumbers,
                                        recaptchaToken
                                    },
                                    {
                                        signal: this.abortController.signal
                                    }
                                )
                                    .then(response => {
                                        this.abortController = null
                                        let data = response.data
                                        if (data) {
                                            for (let i = 0; i < data.length; i++) {
                                                this.questions[index + i].tts = data[i]
                                            }
                                        }
                                        if (this.loading.isActive) {
                                            this.stopLoading()
                                            this.$nextTick(() => {
                                                $('#number-input').blur().focus()
                                                this.ttsPlay()
                                            })
                                        }
                                    })
                                    .catch(error => {
                                        // Request will be canceled only for making another one, so
                                        // stop the loading only in case of other errors
                                        if (error.code !== 'ERR_CANCELED') {
                                            console.log(error)
                                            this.errorOccurred = true
                                            this.errorMessage = error?.response?.data?.message
                                            this.stopLoading()
                                        }
                                    })
                            })
                            .catch((error) => {
                                console.log(error)
                                this.errorOccurred = true
                                this.stopLoading()
                            })
                    })
                }
            }
        }
    },
    created() {
        this.audioElement = new Audio
    },
    mounted() {
        // Input Enter keypress handler
        $('.train-numbers-main-wrapper').on('keypress', '#number-input', (e) => {
            if (e.which === 13 || e.keyCode === 13) {
                this.checkAnswer()
            }
        })
    },
    methods: {
        // Clear everything, fetch new questions and show the first one
        fetchNumbersData() {
            this.abortController?.abort()
            this.abortController = new AbortController()
            this.fireworksClear()
            this.answerToCheck = ''
            this.quizDone = false
            this.questions = []
            this.currentQuestionIndex = null
            // If recaptcha has not been initialized, show an error and skip the request
            if (!grecaptcha) {
                this.errorOccurred = true
                this.stopLoading()
                return
            }
            grecaptcha.ready(() => {
                grecaptcha.execute('6Lcbo5MkAAAAAB8UQm-cCOn8oWoLMUoan1b5yN-u', {action: 'train_numbers'})
                    .then((recaptchaToken) => {
                        axios.post(
                            '/api/training/numbers/generate-questions',
                            {
                                ...this.params,
                                recaptchaToken
                            },
                            {
                                signal: this.abortController.signal
                            }
                        )
                            .then(response => {
                                this.abortController = null
                                this.questions = response.data.numbers
                                this.currentQuestionIndex = 0
                                this.stopLoading()
                                this.$nextTick(() => {
                                    $('#number-input').focus()
                                    this.ttsPlay()
                                })
                            })
                            .catch(error => {
                                // Request will be canceled only for making another one, so
                                // stop the loading only in case of other errors
                                if (error.code !== 'ERR_CANCELED') {
                                    console.log(error)
                                    this.errorOccurred = true
                                    this.errorMessage = error?.response?.data?.message
                                    this.stopLoading()
                                }
                            })
                    })
                    .catch((error) => {
                        console.log(error)
                        this.errorOccurred = true
                        this.stopLoading()
                    })
            })
        },

        // Update input value with validated data
        inputMaskOnAccept(e) {
            this.answerToCheck = e.detail.value
        },

        // Validate and check the given answer
        checkAnswer() {
            // Check if the element is valid
            if (this.answerToCheck === '') {
                // If it's not, then show a warning to the user
                $('#number-input')[0].setCustomValidity(this.inputModes[this.currentInputMode].validationMessage)
                $('#number-input')[0].reportValidity()
                // Hide the warning on input
                $('#number-input').on('keydown', function() {
                    $(this)[0].setCustomValidity('')
                    $(this).off('keydown')
                })
                return
            }
            // Check the answer and move next or show an error
            this.currentQuestion.answer = this.answerToCheck
            this.currentQuestion.answerCorrect = this.currentQuestion.index === Number(this.answerToCheck)
            if (!this.params.correctInstantly || this.currentQuestion.answerCorrect) {
                this.goToNextQuestion()
            } else {
                this.$nextTick(() => {
                    $('.btn.next-question').focus()
                })
            }
        },

        // Show the next question or mark quiz as done
        goToNextQuestion() {
            this.answerToCheck = ''
            if (this.currentQuestionIndex + 1 < this.questions.length) {
                this.currentQuestionIndex++
                this.$nextTick(() => {
                    if (!this.loading.isActive) {
                        $('#number-input').blur().focus()
                        this.ttsPlay()
                    }
                })
            } else {
                this.quizDone = true
                this.$nextTick(() => {
                    if (this.questions.filter(el => el.answerCorrect).length === this.questions.length) {
                        this.fireworks = new Fireworks($('.train-fireworks')[0], {
                            acceleration: 1.01,
                            flickering: 0,
                            intensity: 20,
                            traceSpeed: 5
                        })
                        this.fireworks.start()
                    }
                    $('.start-over').focus()
                })
            }
        },

        // Clear everything and load new data according to the current settings
        startOver() {
            /* WITH THE LEADING ZERO, THERE IS ALWAYS 1 NUMBER OUT OF RANGE */
            // Don't load new data in some cases
            // if (
            //     (Number(this.params.questionsQuantity) === 10 && Number(this.params.range) === 10)
            //     || (Number(this.params.questionsQuantity) === 20 && Number(this.params.range) === 20)
            // ) {
            //     this.fireworksClear()
            //     // Just shuffle the same questions
            //     this.questions.map(el => el.answerCorrect = undefined)
            //     this.questions = this.questions.shuffle()
            //     this.quizDone = false
            //     this.currentQuestionIndex = 0
            //     this.$nextTick(() => {
            //         $('#number-input').focus()
            //         this.ttsPlay()
            //     })
            // } else {
                // Load new numbers
                this.startLoading('Генерация чисел')
                this.fetchNumbersData()
            // }
        },

        // Handler after saving settings
        settingsSaved(initialSave) {
            this.startLoading(initialSave ? 'Запуск' : 'Применение настроек')
            // Load new numbers
            this.fetchNumbersData()
        },

        // Embed and run audio for the current question
        ttsPlay() {
            if (this.currentQuestion.tts) {
                // Set the received audio url
                this.audioElement.src = this.currentQuestion.tts
                // Load the audio
                this.audioElement.load()
                // Turn it on automatically
                this.audioElement.play().catch(() => {
                    this.errorOccurred = true
                })
            }
        },

        // The "Open settings" button handler
        settingsOpen() {
            $('.settings-wrapper').show().removeClass('closed')
        },

        // Start general loading
        startLoading(message = 'Загрузка') {
            this.stopLoading()
            this.loading.message = message
            this.loading.isActive = true
            let data = this
            this.loading.dotsTimeout = setTimeout(function addDot() {
                if (data.loading.dots.length < 3) {
                    data.loading.dots += '.'
                } else {
                    data.loading.dots = ''
                }
                data.loading.dotsTimeout = setTimeout(addDot, 300)
            }, 300)
        },

        // Stop general loading
        stopLoading() {
            this.loading.isActive = false
            this.loading.message = ''
            clearTimeout(this.loading.dotsTimeout)
            this.loading.dotsTimeout = null
            this.loading.dots = ''
        },

        // Delete fireworks
        fireworksClear() {
            if (this.fireworks) {
                this.fireworks.clear()
                this.fireworks = null
            }
        },

        // Show/hide results table
        toggleResultsTable() {
            $('.results-table-wrapper').toggle()
            if ($('.results-table-wrapper:visible').length) {
                $('.results-table-link-wrapper').removeClass('show').addClass('hide')
                $('.results-table-link-wrapper a').text('Скрыть подробности')
            } else {
                $('.results-table-link-wrapper').removeClass('hide').addClass('show')
                $('.results-table-link-wrapper a').text('Показать подробности')
            }
        }
    },
}
</script>

<style scoped lang="scss">
    .train-numbers-main-wrapper {
        position: relative;
        display: flex;
        overflow: hidden;
        height: 60vh;
        min-height: 350px;
        max-height: 710px;
        padding: 15px;
        border: 1px solid #eee;
        .settings-open {
            position: absolute;
            top: 0;
            right: 0;
            display: flex;
            width: 33px;
            height: 34px;
            align-items: center;
            justify-content: center;
        }
        .question-number {
            position: absolute;
            top: 6px;
            left: 9px;
            color: #999;
            font-size: 16px;
            line-height: 18px;
        }
        .train-question {
            margin-bottom: 32px;
            font-size: 3rem;
            line-height: 3.4rem;
            text-align: center;
            @media (width < 768px) {
                font-size: 2rem;
                line-height: 2.5rem;
            }
            @media (width < 398px) {
                font-size: 1.5rem;
                line-height: 2.2rem;
            }
        }
        .answer-wrapper.number-mode {
            width: 180px;
            .wrong-answer-wrapper {
                position: relative;
                width: 250px;
            }
        }
        .results-table-outer-wrapper {
            .results-table-link-wrapper {
                &.hide {
                    @media (width >= 576px) {
                        text-align: right;
                    }
                    @media (width < 576px) {
                        margin: 4px 0;
                    }
                }
                &.show a {
                    margin: 10px 0;
                }
                a {
                    display: inline-block;
                    transition: none;
                    @media (hover: hover) {
                        &:hover {
                            text-decoration: underline;
                        }
                    }
                }
            }
            .results-table-wrapper {
                overflow: auto;
                height: calc(60vh - 205px);
                min-height: 150px;
                max-height: 460px;
                margin: 7px auto 2px;
                background: #fff;
                scrollbar-width: thin;
                .table {
                    margin: 0;
                    text-align: left;
                    white-space: nowrap;
                    thead {
                        th {
                            position: sticky;
                            z-index: 5;
                            top: 0;
                            border: none;
                        }
                    }
                    .results-answer {
                        display: inline-block;
                        padding: 0 3px;
                        margin: 0;
                        &.number-mode {
                            min-width: 42px;
                        }
                    }
                }
            }
        }
        .start-over {
            margin-top: 10px;
        }
        .train-fireworks {
            position: absolute;
            z-index: -1;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            opacity: .3;
        }
    }
</style>
